import { action, extendObservable } from 'mobx';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import NotificationService from '../../../core/services/NotificationService';
import BillPaymentDialogService from './BillPaymentDialogService';
import BillCancellationDialogService from './BillCancellationDialogService';

class BillListService {
  submit = new FetchService();
  fetch = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      bills: [],
      selectedBills: [],
      pagination: {
        current: 1,
        pageSize: 50,
        total: 0,
      },
      sort: { field: 'billId', order: 'ascend' },
      sortedInfo: {},
    });
  }

  init = action(() => {
    this.form.reset();
    this.pagination = {
      ...this.pagination,
      current: 1,
    };
  });

  load = action(() => {
    this.fetch
      .fetch({
        url: '/bills',
        apiVersion: 'v2',
        query: {
          ...{
            ...(this.form.getValue('billId') && { billId: this.form.getValue('billId') }),
            ...(this.form.getValue('documentNumber') && {
              documentNumber: this.form.getValue('documentNumber'),
            }),
            ...(this.form.getValue('socialReason') && {
              socialReason: this.form.getValue('socialReason'),
            }),
            ...(this.form.getValue('status') && {
              status: this.form.getValue('status'),
            }),
            ...((this.form.getValue('dueStart') || this.form.getValue('dueEnd')) && {
              dueFrom: this.form.getValue('dueStart') ? this.form.getValue('dueStart').toISOString() : {},
              dueTo: this.form.getValue('dueEnd') ? this.form.getValue('dueEnd').toISOString() : {},
            }),
            ...((this.form.getValue('createdStart') || this.form.getValue('createdEnd')) && {
              createdFrom: this.form.getValue('createdStart') ? this.form.getValue('createdStart').toISOString() : {},
              createdTo: this.form.getValue('createdEnd') ? this.form.getValue('createdEnd').toISOString() : {},
            }),
            ...(this.form.getValue('overduePayments') && { overduePayments: true }),
          },
          page: this.pagination.current,
          size: this.pagination.pageSize,
          sort: this.sort,
        },
      })
      .then(() => {
        if (this.fetch.data) {
          this.bills = this.fetch.data.docs;
          this.pagination = { ...this.pagination, total: this.fetch.data.total };
        } else {
          this.bills = [];
          this.pagination = { ...this.pagination, total: 0 };
        }
      });
  });

  downloadBillReport = action(async () => {
    ConfirmationDialogService.show(
      'Export Bill Report',
      `Do you really want to export this report? (${this.pagination.total} bills)`,
      () => {
        this.fetch
          .fetch({
            url: '/reports/bills',
            apiVersion: 'v2',
            responseType: 'blob',
            query: {
              ...{
                ...(this.form.getValue('billId') && { billId: this.form.getValue('billId') }),
                ...(this.form.getValue('documentNumber') && {
                  documentNumber: this.form.getValue('documentNumber'),
                }),
                ...(this.form.getValue('socialReason') && {
                  socialReason: this.form.getValue('socialReason'),
                }),
                ...(this.form.getValue('status') && {
                  status: this.form.getValue('status'),
                }),
                ...((this.form.getValue('dueStart') || this.form.getValue('dueEnd')) && {
                  dueFrom: this.form.getValue('dueStart') ? this.form.getValue('dueStart').toISOString() : {},
                  dueTo: this.form.getValue('dueEnd') ? this.form.getValue('dueEnd').toISOString() : {},
                }),
                ...((this.form.getValue('createdStart') || this.form.getValue('createdEnd')) && {
                  createdFrom: this.form.getValue('createdStart')
                    ? this.form.getValue('createdStart').toISOString()
                    : {},
                  createdTo: this.form.getValue('createdEnd') ? this.form.getValue('createdEnd').toISOString() : {},
                }),
                ...(this.form.getValue('overduePayments') && { overduePayments: true }),
              },
              sort: this.sort,
            },
          })
          .then((data) => {
            if (this.fetch.data) {
              const link = document.createElement('a');
              const fileUrl = window.URL.createObjectURL(data);
              const filename = `bills_${Date.now().toString()}.csv`;

              link.href = fileUrl;
              link.download = filename;
              link.click();
              setTimeout(() => window.URL.revokeObjectURL(fileUrl), 500);
            }
          });
      }
    );
  });

  handleAction = (action, data) => {
    switch (action) {
      case 'select':
        this.handleSelect(data);
        break;
      case 'open':
        this.handleOpen(data);
        break;
      case 'send':
        this.handleSendToClient(data);
        break;
      case 'download':
        this.handleDownload(data);
        break;
      case 'delete':
        this.handleDelete(data);
        break;
      case 'pay':
        this.handlePay(data);
        break;
      case 'gift':
        this.handleNoCharge(data);
        break;
      case 'cancel':
        this.handleCancel(data);
        break;
      default:
        break;
    }
  };

  handleSelect = action((selectedBills) => {
    this.selectedBills = [...selectedBills];
  });

  handleNoCharge = (bill) => {
    ConfirmationDialogService.show(
      'No Charge Bill',
      `Would you really like change the bill ${bill.billId} status to 'No Charge'?`,
      async () => {
        this.submit
          .fetch({
            method: 'put',
            url: `/bills/${bill.id}/no-charge`,
            apiVersion: 'v2',
          })
          .then(() => {
            if (this.submit.data) {
              NotificationService.addNotification(`Bill marked as no charge successfully.`, 'success');
              this.load();
            }
            if (this.submit.error) {
              NotificationService.addNotification(`Error marking Bill as no charge.`, 'error');
            }
          });
      }
    );
  };

  handlePay = (bill) => {
    BillPaymentDialogService.handleOpen(bill);
  };

  handleCancel = (bill) => {
    if (bill) {
      BillCancellationDialogService.handleOpen(bill);
    }
  };

  handleSendToClient = (bill) => {
    ConfirmationDialogService.show(
      bill ? `Bill ${bill.billId}` : 'Bills',
      bill
        ? `Would you really like to send the bill ${bill.billId} to client?`
        : 'This action cannot be undone. Only "Open" bills will be sent to clients and the bill’s PDFs will be available in few minutes. Do you really want to send selected bills to clients?',
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'put',
              url: bill ? `/bills/${bill.id}/send-to-client` : '/bills/send-to-client/batch',
              apiVersion: 'v2',
              ...(!bill && {
                body: {
                  bills: this.selectedBills
                    .map((selectedBill) => this.bills.find((b) => b.id === selectedBill))
                    .filter((selectedBill) => selectedBill.status === 'OPEN')
                    .map((selectedBill) => selectedBill.id),
                },
              }),
            })
            .then(() => {
              if (this.submit.data) {
                if (bill) {
                  NotificationService.addNotification(`Bill sent to client successfully.`, 'success');
                } else {
                  NotificationService.addNotification(`Bills sent to client successfully.`, 'success');
                }
                this.load();
              }
              if (this.submit.error) {
                if (bill) {
                  NotificationService.addNotification(`Error sending Bill to client.`, 'error');
                } else {
                  NotificationService.addNotification(`Error sending Bills to client.`, 'error');
                }
              }
              resolve();
            });
        });
      }
    );
  };

  handleDownload = (billUrl) => {
    const filePath = `${process.env.REACT_APP_FILES_URL}/${billUrl}`;
    const link = document.createElement('a');
    link.href = filePath;
    link.download = filePath.split('/').pop();
    link.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(filePath);
      link.remove();
    }, 500);
  };

  handleOpen = (bill) => {
    ConfirmationDialogService.show('Open Bill', `Would you really like to reopen the bill ${bill.billId}?`, () => {
      this.submit
        .fetch({
          method: 'put',
          url: `/bills/${bill.id}/reopen`,
          apiVersion: 'v2',
        })
        .then(() => {
          if (this.submit.data) {
            NotificationService.addNotification(`Bill opened successfully.`, 'success');
            this.load();
          }
          if (this.submit.error) {
            NotificationService.addNotification(`Error opening Bill.`, 'error');
          }
        });
    });
  };

  handleDelete = (bill) => {
    ConfirmationDialogService.show('Delete Bill', `Do you really want to delete bill ${bill.billId}?`, () => {
      this.submit
        .fetch({
          method: 'delete',
          url: `/bills/${bill.id}`,
          apiVersion: 'v2',
        })
        .then(() => {
          if (this.submit.data) {
            this.load();
          }
        });
    });
  };

  handleChange = action((pagination, filters, sort) => {
    this.pagination = { ...this.pagination, ...pagination };
    this.sort = sort;
    this.load();
  });
}

const billListService = new BillListService();

export default billListService;
